import { getEnv } from '@/utils/helpers'
import { appendUrlQueryParams } from '@/utils/url.utils'
import type { ClientActionEvent, ToolCallInfo } from '../types'

function utf8ToBase64(str: string): string {
    return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) => String.fromCharCode(parseInt(p1, 16))))
}

export interface ElaraTransportCallbacks {
    onProcessingChange: (processing: boolean) => void
    onResponse: (response: string) => void
    onClientAction: (event: ClientActionEvent) => void
    /** Called on every individual token delta — drives streaming display */
    onStreamToken: (token: string, accumulated: string) => void
    /** Called when the phase transitions: thinking → tool-calling → streaming → done */
    onPhaseChange: (phase: 'thinking' | 'tool-calling' | 'streaming' | 'done' | 'error', error?: string) => void
    /** Called when tool call events arrive */
    onToolEvent: (event: 'call' | 'start' | 'end', tools: Array<{ id: string; name: string; args?: any }>, toolResult?: string) => void
}

interface ElaraTransportConfig extends ElaraTransportCallbacks {
    consultationId: string
    getAccessToken: () => string | null
    agentType?: string
    /** LLM provider override (e.g., 'openai', 'bedrock') */
    model?: string
}

export function createElaraTransport(config: ElaraTransportConfig) {
    let eventSource: EventSource | null = null
    let sessionId: string | null = null

    function close() {
        if (eventSource) {
            eventSource.close()
            eventSource = null
        }
        config.onProcessingChange(false)
    }

    function send(message: string, businessMeta: Record<string, any>) {
        close()
        config.onProcessingChange(true)
        config.onPhaseChange('thinking')

        const accessToken = config.getAccessToken() || ''

        const sessionPath = sessionId ? `/${sessionId}` : ''
        const baseUrl = `${getEnv('VITE_API_URL') || 'http://localhost:3000'}/medconnect-agent/chat/stream${sessionPath}`

        const queryParams: Record<string, string> = {
            message,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            agentType: config.agentType || 'patient-consultation',
        }

        if (accessToken) {
            queryParams.authorization = `Bearer ${encodeURIComponent(accessToken)}`
        }

        queryParams.businessMeta = utf8ToBase64(JSON.stringify(businessMeta))

        if (config.model) {
            queryParams.model = config.model
        }

        const streamUrl = appendUrlQueryParams(baseUrl, queryParams)
        const es = new EventSource(streamUrl)
        eventSource = es

        let fullResponse = ''
        let hasReceivedToken = false

        es.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data)

                if (data.type === 'heartbeat') return

                // Token deltas — streaming text
                if (data.isDelta && typeof data.token === 'string') {
                    if (!hasReceivedToken) {
                        hasReceivedToken = true
                        config.onPhaseChange('streaming')
                    }
                    fullResponse += data.token
                    config.onStreamToken(data.token, fullResponse)
                    config.onResponse(fullResponse)
                }

                // Tool call announced — LLM decided to use tools
                if (data.toolEvent === 'call' && Array.isArray(data.toolCalls)) {
                    config.onPhaseChange('tool-calling')
                    config.onToolEvent('call', data.toolCalls)
                }

                // Tool execution started
                if (data.toolEvent === 'start' && Array.isArray(data.toolCalls)) {
                    config.onToolEvent('start', data.toolCalls)
                }

                // Tool execution ended
                if (data.toolEvent === 'end') {
                    const tools = Array.isArray(data.toolCalls) ? data.toolCalls : []
                    config.onToolEvent('end', tools, data.toolResult)

                    // Check for client actions in tool result
                    if (data.toolResult) {
                        try {
                            const result = typeof data.toolResult === 'string'
                                ? JSON.parse(data.toolResult)
                                : data.toolResult
                            if (result.type === 'client_action') {
                                config.onClientAction({
                                    type: 'client_action',
                                    actionId: result.actionId,
                                    params: result.params,
                                })
                            }
                        } catch {
                            // Tool result is not JSON — ignore
                        }
                    }
                }

                // Capture sessionId
                if (data.sessionId && !sessionId) {
                    sessionId = data.sessionId
                }

                // Error
                if (data.error) {
                    config.onPhaseChange('error', data.error)
                    close()
                    return
                }

                // Completion
                if (data.complete) {
                    // Non-streaming paths (e.g. Mastra) send the full response in data.output
                    const finalText = fullResponse || data.output || ''
                    if (finalText && !hasReceivedToken) {
                        config.onPhaseChange('streaming')
                    }
                    config.onPhaseChange('done')
                    close()
                    if (finalText) {
                        config.onResponse(finalText)
                    }
                }
            } catch (error) {
                console.error('[ElaraTransport] SSE parsing error:', error)
            }
        }

        es.onerror = () => {
            console.error('[ElaraTransport] SSE connection error')
            config.onPhaseChange('error', 'Connection lost')
            close()
        }
    }

    return { send, close }
}
